export const defaultOffcanvasOptions = {
    zIndex: 56,
};
// Per-offcanvas teardown (Escape/Tab keydown, overlay-click, focus restore).
// Stored so hideOffcanvas() can detach exactly what showOffcanvas() attached.
const offcanvasCleanups = new WeakMap();
// Visible, focusable elements inside the panel (skips collapsed accordions, and
// anything inside an inert subtree such as a collapsed user-menu).
function getFocusable(container) {
    const selector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll(selector)).filter((el) => el.offsetParent !== null && !el.closest('[inert]'));
}
export function showOffcanvas(offcanvas, options) {
    const offcanvasEl = typeof offcanvas === 'string' ? document.getElementById(offcanvas) : offcanvas;
    if (!offcanvasEl)
        return;
    const offcanvasPanelEl = offcanvasEl.querySelector('.offcanvas-panel') ?? null;
    if (!offcanvasPanelEl)
        return;
    const opts = { ...defaultOffcanvasOptions, ...options };
    offcanvasEl.style.zIndex = opts.zIndex.toString();
    offcanvasPanelEl.style.zIndex = (opts.zIndex + 1).toString();
    offcanvasEl.classList.remove('hidden');
    document.body.classList.add('no-scroll');
    // Remember what had focus so it can be restored when the drawer closes.
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Tap on the dark overlay (anywhere outside the panel) closes the drawer.
    const onOverlayClick = (e) => {
        if (e.target instanceof Node && !offcanvasPanelEl.contains(e.target)) {
            hideOffcanvas(offcanvasEl);
        }
    };
    // Escape closes; Tab is trapped within the panel.
    const onKeydown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            hideOffcanvas(offcanvasEl);
            return;
        }
        if (e.key !== 'Tab')
            return;
        const focusable = getFocusable(offcanvasPanelEl);
        if (focusable.length === 0) {
            e.preventDefault();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (!active || !offcanvasPanelEl.contains(active)) {
            e.preventDefault();
            first.focus();
        }
        else if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
        }
        else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    };
    offcanvasEl.addEventListener('click', onOverlayClick);
    document.addEventListener('keydown', onKeydown);
    offcanvasCleanups.set(offcanvasEl, () => {
        offcanvasEl.removeEventListener('click', onOverlayClick);
        document.removeEventListener('keydown', onKeydown);
        if (previouslyFocused && document.contains(previouslyFocused)) {
            previouslyFocused.focus();
        }
    });
    setTimeout(() => {
        offcanvasEl.classList.add('active');
        // Move focus into the panel for keyboard / screen-reader users.
        (getFocusable(offcanvasPanelEl)[0] ?? offcanvasPanelEl).focus();
    }, 50);
}
export function hideOffcanvas(offcanvas) {
    const offcanvasEl = typeof offcanvas === 'string' ? document.getElementById(offcanvas) : offcanvas;
    if (!offcanvasEl)
        return;
    const offcanvasPanelEl = offcanvasEl.querySelector('.offcanvas-panel') ?? null;
    if (!offcanvasPanelEl)
        return;
    const cleanup = offcanvasCleanups.get(offcanvasEl);
    if (cleanup) {
        cleanup();
        offcanvasCleanups.delete(offcanvasEl);
    }
    offcanvasEl.classList.remove('active');
    setTimeout(() => {
        offcanvasEl.classList.add('hidden');
        document.body.classList.remove('no-scroll');
    }, 300);
}
